import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import Card from '../Card';
import If from '../../../components/If/If';
import DateTimePicker from '../../../components/Form/DateTimePicker';
import ModalComponent from '../../../components/ModalComponent';
import {
  deactivateFreeze, FreezeSetting, getCurrentFreeze, setFreeze,
} from '../../../services/confirmFreeze';
import { useGlobalContext } from '../../../contexts/GlobalContext';

function ConfirmFreeze() {
  const { refreshFreezeStatus } = useGlobalContext();
  const [currentSetting, setCurrentSetting] = useState<FreezeSetting | null>(null);
  const [startDatetime, setStartDatetime] = useState('');
  const [endDatetime, setEndDatetime] = useState('');
  const [pickerKey, setPickerKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const loadCurrent = async () => {
    setLoading(true);
    try {
      const response = await getCurrentFreeze();
      const setting = response?.data?.id ? response.data : null;
      setCurrentSetting(setting);
      if (setting) {
        setStartDatetime(dayjs(setting.start_datetime).toISOString());
        setEndDatetime(dayjs(setting.end_datetime).toISOString());
        setPickerKey((prev) => prev + 1);
      }
    } catch (e) {
      // handled by the global interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrent();
  }, []);

  const openActivateModal = () => {
    setErrorMessage('');
    if (!startDatetime || !endDatetime) {
      setErrorMessage('開始日時と終了日時を入力してください');
      return;
    }
    if (!dayjs(endDatetime).isAfter(dayjs(startDatetime))) {
      setErrorMessage('終了日時は開始日時より後に設定してください');
      return;
    }
    if (!dayjs(endDatetime).isAfter(dayjs())) {
      setErrorMessage('終了日時は現在より後に設定してください');
      return;
    }
    setShowActivateModal(true);
  };

  const activate = async () => {
    setErrorMessage('');
    setSaving(true);
    try {
      const response = await setFreeze({
        start_datetime: dayjs(startDatetime).toISOString(),
        end_datetime: dayjs(endDatetime).toISOString(),
      });
      if (!response?.data) {
        setErrorMessage('通信エラーが発生しました。再度ログインしてください。');
        return;
      }
      await loadCurrent();
      refreshFreezeStatus();
    } catch (e: any) {
      if (e?.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setErrorMessage('');
    setSaving(true);
    try {
      const response = await deactivateFreeze();
      if (!response?.data) {
        setErrorMessage('通信エラーが発生しました。再度ログインしてください。');
        return;
      }
      await loadCurrent();
      refreshFreezeStatus();
    } catch (e: any) {
      if (e?.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const isActive = currentSetting?.is_active === true;
  const getFreezePhase = () => {
    if (!isActive || !currentSetting) {
      return 'inactive';
    }
    if (dayjs().isBefore(dayjs(currentSetting.start_datetime))) {
      return 'scheduled';
    }
    if (dayjs().isAfter(dayjs(currentSetting.end_datetime))) {
      return 'ended';
    }
    return 'active';
  };
  const freezePhase = getFreezePhase();
  const formatPeriod = (setting: FreezeSetting) => `${dayjs(setting.start_datetime).format('YYYY/MM/DD HH:mm')} 〜 ${dayjs(setting.end_datetime).format('YYYY/MM/DD HH:mm')}`;

  return (
    <main className="content p-3">
      <ModalComponent
        title="確定制限有効化確認"
        body="設定した期間で確定・確定解除の操作を制限します。よろしいですか？"
        confirmButtonText="有効化する"
        confirmButtonColor="primary"
        onConfirm={() => activate()}
        show={showActivateModal}
        closeModal={() => setShowActivateModal(false)}
      />
      <ModalComponent
        title="確定制限無効化確認"
        body="現在の確定制限を無効化します。よろしいですか？"
        confirmButtonText="無効化する"
        confirmButtonColor="warning"
        onConfirm={() => deactivate()}
        show={showDeactivateModal}
        closeModal={() => setShowDeactivateModal(false)}
      />
      <Card title="確定制限設定" noHeader>
        <If test={loading}>
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        </If>
        <If test={!loading}>
          <div className="mb-3">
            <span className="fw-bold me-2">現在の状態：</span>
            {freezePhase === 'active' && currentSetting && (
              <span className="text-danger">
                <FontAwesomeIcon icon={faLock} className="me-1" />
                有効・制限中（
                {formatPeriod(currentSetting)}
                ）
              </span>
            )}
            {freezePhase === 'scheduled' && currentSetting && (
              <span className="text-primary">
                <FontAwesomeIcon icon={faLock} className="me-1" />
                有効・開始前（
                {formatPeriod(currentSetting)}
                ）
              </span>
            )}
            {freezePhase === 'ended' && currentSetting && (
              <span className="text-secondary">
                <FontAwesomeIcon icon={faLockOpen} className="me-1" />
                期間終了・制限なし（
                {formatPeriod(currentSetting)}
                ）
              </span>
            )}
            {freezePhase === 'inactive' && (
              <span className="text-success">
                <FontAwesomeIcon icon={faLockOpen} className="me-1" />
                無効
              </span>
            )}
          </div>
          <div className="mb-2 fw-bold">期間設定</div>
          <div className="row row-cols-auto g-1 align-items-center mb-3">
            <label className="col col-form-label">開始日時：</label>
            <div className="col me-3">
              <DateTimePicker
                key={`start-${pickerKey}`}
                id="freeze-start-datetime"
                initialDate={startDatetime ? dayjs(startDatetime).format('YYYY/MM/DD HH:mm') : ''}
                onChange={(value) => setStartDatetime(value)}
              />
            </div>
            <label className="col col-form-label">終了日時：</label>
            <div className="col">
              <DateTimePicker
                key={`end-${pickerKey}`}
                id="freeze-end-datetime"
                initialDate={endDatetime ? dayjs(endDatetime).format('YYYY/MM/DD HH:mm') : ''}
                onChange={(value) => setEndDatetime(value)}
              />
            </div>
          </div>
          <div className="text-danger mb-3">
            { errorMessage }
          </div>
          <Button
            variant="primary"
            className="me-2"
            disabled={saving}
            onClick={() => openActivateModal()}
          >
            {saving && <Spinner size="sm" animation="border" className="me-1" />}
            有効化
          </Button>
          <Button
            variant="warning"
            disabled={saving || !isActive}
            onClick={() => setShowDeactivateModal(true)}
          >
            無効化
          </Button>
        </If>
      </Card>
    </main>
  );
}

export default ConfirmFreeze;
